// @flow

export type Token = string
export type UUID = string
export type Timestamp = string
export type DateString = string
export type Error = Error & {
  +status: string,
  +response: string,
}

export type Dinero = {
  +amount: number,
  +currency: string,
}

export type File = {
  +token: UUID,
  +url: string,
  +updatedAt: Timestamp,
  +filename: string,
  +title: string,
  +name: string,
}

export type AppState = {
  +version: string,
}

export type ListType = "upcoming" | "all"

export type Sex = "m" | "f" | "u"

export type ActionMenuAction = {
  label: string,
  onClick: () => void,
}

// TODO complete
export type Ability = "access_admin" | "api_access_admin" | "api_edit_profile"

export type User = {
  +firstname: string,
  +lastname: string,
  +email: string,
  +token: Token,
  +team: string,
  +googleBusinessURL: string,
  +languageId: number,
  +roles: $ReadOnlyArray<Role>,
  +abilities: $ReadOnlyArray<Ability>,
}

export type UserProfileEdit = {
  +firstname: string,
  +lastname: string,
  +email: string,
}

export type UserPasswordEdit = {
  +password: string,
  +passwordConfirmation: string,
}

export type UserState = {
  +entity: ?User,
}

export type ProductsState = {
  +entities: {
    products: $ReadOnlyArray<ProductsByCategory>,
    media: $ReadOnlyArray<ProductMedia>,
  },
  +toggleds: $ReadOnlyArray<ProductOrSet>,
  +revealeds: $ReadOnlyArray<ProductOrSet>,
}

export type State = {
  +app: AppState,
  +user: UserState,
  +products: ProductsState,
  +baby: BabyReduxState,
  +planning: PlanningState,
  +tasks: TasksState,
  +searchItems: SearchItemsState,
  +babies: BabiesState,
  +babyListInvoice: BabyListInvoiceState,
  +invoice: InvoiceState,
  +shop: ShopState,
}

export type Language = {
  +id: number,
  +name: string,
  +iso: "nl-BE" | "fr-BE",
}

export type ValidationError = {
  +type: "required",
}

export type FileType = "image/jpeg"

export type ProductMedia = {
  +id: number,
  +isDefault: boolean,
  +filename: string,
  +title: string,
  +type: FileType,
  +sizes: {
    small: string,
    medium: string,
    large: string,
    original: string,
  },
}

export type FreeProduct = {
  // Product
  +commissionTypeId: number,
  +description: string,
  +isFirstPickup: boolean,
  +isNew: boolean,
  +isPriceTbd: boolean,
  +name: string,
  +priceExtra: string,
  +priceNew: string,
  +pricePimp: string,
  +priceSelling: string,
  +vatTypeId: number,
  // Baby list item
  +amount: number,
  +isStar: boolean,
  +isPimp: boolean,
  +isExtra: boolean,
  +remarks: string,
}

export type Product = {
  +categoryId: number,
  +commissionTypeId: number,
  +description: string,
  +hasMedia: boolean,
  +id: number,
  +isActive: boolean,
  +isAlsoMaster: boolean,
  +isCircular: boolean,
  +isExternal: boolean,
  +isExtra: boolean,
  +isFirstPickup: boolean,
  +isHidden: boolean,
  +isNew: boolean,
  +isPriceTbd: boolean,
  +isService: boolean,
  +isSet: false,
  +media: $ReadOnlyArray<any>,
  +name: string,
  +needTypeId: number,
  +order: number,
  +priceExtra: string,
  +priceNew: string,
  +pricePimp: string,
  +priceSelling: string,
  +productSetId?: number,
  +remarks: string,
  +serviceTypeId: number,
  +subname: string,
  +typicalAmount: number,
  +vatTypeId: number,
  +webshopSource: string,
}

export type ProductSet = {
  +id: number,
  +name: string,
  +subname: string,
  +isFirstPickup: boolean,
  +isHidden: boolean,
  +isPimpable: boolean,
  +isDivisible: boolean,
  +hasMedia: boolean,
  +typicalAmount: number,
  +description: string,
  +order: number,
  +pricePimp: string,
  +categoryId: number,
  +needTypeId: number,
  +products: $ReadOnlyArray<ProductSetProduct>,
  +media: $ReadOnlyArray<any>,
  +isSet: true,
}

export type ProductSetProduct = {
  +id: number,
  +amount: number,
  +order: number,
  +product: Product,
}

export type ProductCategory = {
  +id: number,
  +name: string,
  productCount: ?number,
}

export type ProductOrSet = Product | ProductSet

export type ProductsByCategory = ProductCategory & {
  +products: $ReadOnlyArray<ProductOrSet>,
}

export type TasksState = {
  +entities: $ReadOnlyArray<Task>,
}

export type Task = {
  +id: number,
  +description: string,
  +isDone: boolean,
  +date: ?DateString,
  +type: TaskType,
}

export type TaskType = "madam" | "client" | "general" | "auto"

export type IntakeState = {
  +babyToken: ?string,
}

export type BabyReduxState = {
  +baby: ?Baby,
  +list: ?BabyList,
  +optionals: $ReadOnlyArray<Product>,
  +offerSending: boolean,
  +offers: $ReadOnlyArray<BabyListOffer>,
  +survey: ?Survey,
  +isIntakeSaving: ?boolean,
  +isBabySaving: ?boolean,
}

export type BabyState =
  | "new"
  | "called"
  | "dispatched"
  | "intake_planned"
  | "intake_done"
  | "lost"
  | "born"
  | "confirmed"
  | "signed"
  | "partially_signed"
  | "evaluation_taken"
  | "evaluation_sent"
  | "born"

export const filterableBabyStates = {
  new: ["new"],
  called: ["called"],
  dispatched: ["dispatched"],
  intake_planned: ["intake_planned"],
  intake_done: ["intake_done"],
  lost: ["lost"],
  confirmation_sent: ["confirmation_sent"],
  confirmed: ["confirmed"],
  signed: ["signed"],
  born: ["born"],
  partially_signed: ["partially_signed"],
  prospect: ["prospect", "new"],
  active_untill_online: ["called", "intake_planned"],
}

export type BabiesFilterType =
  | "new"
  | "called"
  | "prospect"
  | "dispatched"
  | "intake_planned"
  | "intake_done"
  | "lost"
  | "signed"
  | "confirmed"
  | "signed"
  | "partially_signed"
  | "active_untill_online"

export type Baby = {
  +id: number,
  +birthDate: ?DateString,
  +dueDate: ?DateString,
  +intakeDate: ?DateString,
  +intakeLocation: ?string,
  +pickupDate: ?DateString,
  +family: Family,
  +firstname: ?string,
  +lastname: ?string,
  +remarks: string,
  +sex: ?Sex,
  +sex_2: ?Sex,
  +sex_3: ?Sex,
  +firstname_2: ?string,
  +firstname_3: ?string,
  +state: BabyState,
  +moodboardType: MoodboardType,
  +babyListId: number,
  +token: string,
  +madam: BabyMadam,
  states?: $ReadOnlyArray<BabyState>,
}

export type BabyMadam = {
  +id: number,
  +name: string,
}

export type MoodboardType =
  | "scandinavian"
  | "colourful"
  | "retroromantic"
  | "just_though"
  | "colour_scandi"
  | "colour_tough"
  | "classic"
  | "colour_classic"
  | "boho"

export type BabiesFilters = {
  +state?: $ReadOnlyArray<BabyState>,
  +madamId?: number,
}

export type BabiesStateFilterType = "called"

export type BabiesSortBy = {
  +property: BabiesSortByProperty,
  +order: BabiesSortByOrder,
}

export const BabiesSortByProperties = ["state", "pickupDate"]

export type BabiesSortByProperty = "state" | "name" | "pickupDate"
export type BabiesSortByOrder = "asc" | "desc" | "default"

export type BabiesState = {
  +madams: $ReadOnlyArray<BabyMadam>,
  +entities: $ReadOnlyArray<Baby>,
  +filters: BabiesFilters,
  +query: string,
  +page: number,
  +totalPages: number,
  +page: number,
  +listType: string,
  +sortBy: BabiesSortBy,
}

export type CountryCode = "NL" | "BE" | "DE" | "FR" | "GB" | "LU"

export type Family = {
  +bossId: number,
  +box: string,
  +childrenAmount: number,
  +city: string,
  +nbr: string,
  +postalCode: string,
  +remarks: ?string,
  +street: string,
  +country: CountryCode,
  +name: string,
  +users: $ReadOnlyArray<FamilyUser>,
  +shouldMailAll: boolean,
}

export type FamilyUser = {
  +email: string,
  +firstname: string,
  +languageId: number,
  +lastname: string,
  +phone: string,
  +token: string,
}

export type BabyListOffer = {
  +version: string,
  +insertedAt: Timestamp,
}

export type Survey = $ReadOnlyArray<BabyAnswer>

export type QuestionType = "list" | "free" | "boolean" | "free_text"

export type BabyAnswer = {
  +babyQuestionId: number,
  +babyAnswerId: number,
  +title: string,
  +description: string,
  +answer: string,
  +remarks: string,
  +isIntake: boolean,
  +isIntakeEmail: boolean,
  +type: QuestionType,
  +options: $ReadOnlyArray<BabyQuestionOption>,
}

export type BabyQuestionOption = {
  +description: string,
  +key: string,
  +value: string,
}

export type BabyList = {
  +items: $ReadOnlyArray<BabyListItem>,
  +total: string,
}

export type BabyListItem = {
  +amount: number,
  +babyListId: number,
  +categoryId: number,
  +commissionTypeId: number,
  +description: string,
  +discount: number,
  +id: number,
  +inEstimate: boolean,
  +isAlsoMaster: boolean,
  +isCircular: boolean,
  +isEditableOnIntake: boolean,
  +isExternal: boolean,
  +isExtra: boolean,
  +isFirstPickup: boolean,
  +isHidden: boolean,
  +isInShop: boolean,
  +isNew: boolean,
  +isPimp: boolean,
  +isPriceTbd: boolean,
  +isService: boolean,
  +isStar: boolean,
  +items: $ReadOnlyArray<BabyListItem>,
  +languageId: number,
  +name: string,
  +needTypeId: number,
  +order: number,
  +parentId: ?number,
  +parentId: number,
  +priceExtra: string,
  +priceNew: string,
  +pricePimp: string,
  +priceSelling: string,
  +productId: ?number,
  +productSetId: ?number,
  +remarks: string,
  +serviceTypeId: number,
  +subname: string,
  +total: string,
  +vatTypeId: number,
  +webshopSource: string,
  +canInvoice: boolean,
}

export type ShopProduct = {
  +id: number,
  +name: string,
  +subname: string,
  +priceSelling: string,
  +parts: number,
  +media: $ReadOnlyArray<Media>,
}

export type SearchItem = {
  +key: string,
  +id: number,
  +uniqId: number,
  +isSet: boolean,
  +isFirstPickup: boolean,
  +isPriceTbd: boolean,
  +isPimp: boolean,
  +isStar: boolean,
  +isExtra: boolean,
  +isCritical: boolean,
  +isRare: boolean,
  +isPhotographed: boolean,
  +isCircular: boolean,
  +remarks: string,
  +discount: number,
  +categoryId: number,
  +category: SearchItemCategory,
  +items: $ReadOnlyArray<SearchItem>,
  +shopProducts: $ReadOnlyArray<ShopProduct>,
  +productId: number,
  +productSetId: ?number,
  +parentId: ?number,
  +isSet: boolean,
  +pricePurchase: string,
  +priceSelling: string,
  +total: string,
  +babyListId: number,
  +babyState: BabyState,
  +babyListItemFoundId: ?number,
  +babyListItemFoundRemarks: string,
  +state: ?BabyListItemFoundStateType,
  +name: string,
  +subname: string,
  // +product: ?SearchItemProduct,
  // +productSet: ?SearchItemProductSet,
  +baby: SearchItemBaby,
}

export type SearchItemCategory = {
  +id: number,
  +name: string,
  +order: number,
}

// export type SearchItemProduct = {
//   +name: string,
//   +subname: string,
//   +order: number,
// }

// export type SearchItemProductSet = {
//   +name: string,
//   +subname: string,
//   +order: number,
// }

export type SearchItemBaby = {
  +id: number,
  +family: SearchItemFamily,
  +moodboardType: ?MoodboardType,
  +pickupDate: string,
  +dueDate: string,
  +remarks: string,
  +madamId: number,
  +sex: Sex,
  +sex_2: Sex,
  +sex_3: Sex,
}

// TODO there are too many props here...
export type SearchItemFamilyUser = {
  +email: string,
  +firstname: string,
  +languageId: number,
  +lastname: string,
  +phone: string,
  +token: string,
}

export type SearchItemFamily = {
  +users: $ReadOnlyArray<SearchItemFamilyUser>,
}

export type BabyListItemFoundStateType = "todo" | "ok" | "ordered" | "missing"

export type SearchListItem = {
  +productId: number,
  +amount: number,
  +categoryId: number,
  +nearestPickupDate: Date,
  +isCritical: boolean,
  +items: $ReadOnlyArray<SearchItem>,
  +hasRemarks: boolean,
}

export type SearchItemsState = {
  +searchListItems: $ReadOnlyArray<SearchListItem>,
  +categories: $ReadOnlyArray<SearchItemCategory>,
  +suppliers: $ReadOnlyArray<Supplier>,
  +allSuppliers: $ReadOnlyArray<Supplier>,
  +toggleds: $ReadOnlyArray<number>,
  +foundRemarksToggleds: $ReadOnlyArray<number>,
}

export type PlanningState = {
  +entities: $ReadOnlyArray<PlanningItem>,
  +babyToken: ?Token,
}

export type PlanningItem = {
  +id: ?number,
  +description: string,
  +key: ?string,
  +type: TaskType,
  +babyId: ?number,
  +date: DateString,
  +dueDate: ?DateString,
  +isActionable: boolean,
  +baby: ?PlanningItemBaby,
}

export type PlanningItemBaby = {
  +family: PlanningItemFamily,
  +madam: PlanningItemMadam,
}

export type PlanningItemFamily = {
  +users: $ReadOnlyArray<PlanningItemFamilyUser>,
}

export type PlanningItemFamilyUser = {
  +firstname: string,
  +lastname: string,
}

export type PlanningItemMadam = {
  +firstname: string,
  +lastname: string,
}

export type BabyListsState = {
  +entities: $ReadOnlyArray<BabyList>,
}

export type ShopState = {
  +shopItems: $ReadOnlyArray<ShopItem>,
  +searchItems: $ReadOnlyArray<SearchItem>,
  +isProcessing: boolean,
  +selectedShopItems: $ReadOnlyArray<number>,
  +selectedSearchItems: $ReadOnlyArray<string>,
  +shopItemMedia: {
    babyMedia: $ReadOnlyArray<Media>,
    shopItemMedia: $ReadOnlyArray<Media>,
  },
  +shop: Shop,
}

export type Shop = {
  +isClosed: boolean,
  +isPublished: boolean,
}

export type ShopItem = {
  +id: number,
  +amount: number,
  +parts: number,
  +sells: number,
  +order: number,
  +name: string,
  +description: string,
  +isPublished: boolean,
  +isSoldOut: boolean,
  +isSticky: boolean,
  +isCash: boolean,
  +priceSelling: string,
  +orderCost: string,
  +babyId: number,
  +shopProductId: number,
  +babyListId: number,
  +babyListItems: $ReadOnlyArray<ShopBabyListItem>,
  +type: ShopItemType,
  +media: $ReadOnlyArray<ShopItemMedia>,
}

export type ShopItemContents = {
  +products: $ReadOnlyArray<ShopItemContentsProduct>,
}

export type ShopItemContentsProduct = {
  +name: string,
}

export type ShopItemMedia = {
  +id: number,
  +mediaId: number,
  +filename: string,
  +order: string,
  +title: string,
  +type: FileType,
  +sizes: {
    small: string,
    medium: string,
    large: string,
    original: string,
  },
}

// TODO difference with productmedia?
export type Media = {
  +id: number,
  +babyId: number,
  +name: string,
  +sizes: {
    small: string,
    medium: string,
    large: string,
    original: string,
  },
}

// this is a sort of alias for a babyListItem
export type ShopBabyListItem = {
  +id: number,
}

export type ToggleOpt = {
  label: string,
  value: any,
}

export type ShopItemType = "single" | "multi" | "group" | "shop-product"

export type Role = "admin" | "madam" | "client" | "superuser"

export type BabyListOptionalItem =
  | {id: number, productId: number}
  | {id: number, productSetId: number}

export type InvoiceBase = {
  +id: number,
  +title: string,
  +invoiceNbr: string,
  +invoiceDate: ?DateString,
  +dueDate: ?DateString,
  +finalisationDate: ?DateString,
  +sender: InvoicePerson,
  +receiver: InvoicePerson,
  +amountDiscount: string,
  +note: string,
  +isFinal: boolean,
  +isPaid: boolean,
  +isEditable: boolean,
}

export type InvoicePerson = {
  +accountNbr: ?string,
  +street: string,
  +nbr: string,
  +box: string,
  +postalCode: string,
  +city: string,
  +country: string,
  +company: string,
  +firstname: ?string,
  +lastname: ?string,
  +email: string,
  +vat: ?string,
  +phone: string,
  +partner1Firstname: ?string,
  +partner1Lastname: ?string,
  +partner2Firstname: ?string,
  +partner2Lastname: ?string,
}

// type InvoiceCompanyPerson = {
//   +accountNbr: ?string,
//   +street: string,
//   +nbr: string,
//   +box: string,
//   +postalCode: string,
//   +city: string,
//   +country: string,
//   +company: string,
//   +firstname: string,
//   +lastname: string,
//   +email: string,
//   +vat: string,
//   +phone: string,
// }
//
// type InvoiceRegularPerson = {
//   +street: string,
//   +nbr: string,
//   +box: string,
//   +postalCode: string,
//   +city: string,
//   +country: string,
//   +email: string,
//   +phone: string,
//   +partner1Firstname: string,
//   +partner1Lastname: string,
//   +partner2Firstname: string,
//   +partner2Lastname: string,
// }

// export type InvoicePerson = InvoiceRegularPerson | InvoiceCompanyPerson

export type BabyListInvoice = InvoiceBase & {
  +babyId: number,
  +amountGift: string,
  +amountDepositLeft: string,
  +amountDeposit: string,
  +amountDiscount: string,
  +hasDepositLeft: boolean,
  +hasDeposit: boolean,
  +hasGift: boolean,
  +hasGiftLeft: boolean,
  +amountGiftLeft: string,
  +isDraftSent: boolean,
  +itemsGrouped: $ReadOnlyArray<BabyListInvoiceCategory>,
  +totals: BabyListInvoiceTotals,
}

export type BabyListInvoiceTotals = {
  +totalItemsInclVat: string,
  +totalItemsExclVat: string,
  +totalInclVat: string,
}

export type BabyListInvoiceCategory = {
  +categoryId: number,
  +categoryName: string,
  +items: $ReadOnlyArray<BabyListInvoiceItem>,
}

export type BabyListInvoiceItem = {
  +id: number,
  +babyListItemId: number,
  +babyListInvoiceId: number,
  +amountDiscount: string,
  +amount: number,
  +cost: string,
  +description: string,
}

export type BabyListInvoiceState = {
  +babyListInvoice: ?BabyListInvoice,
  +itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
  +searchItems: $ReadOnlyArray<BabyListItem>,
  +isProcessing: boolean,
  +finaliseError: boolean,
}

export type Invoice = InvoiceBase & {
  +madamId: ?number,
  +totals: InvoiceTotals,
  +isDraftSent: boolean,
  +itemsGrouped: $ReadOnlyArray<InvoiceItemGroup>,
}

export type InvoiceItemGroup = {
  +key: InvoiceItemKeyType,
  +items: $ReadOnlyArray<InvoiceItem | InvoiceGroupedItem>,
}

export type InvoiceItemKeyType =
  | "baby_list_item"
  | "baby_list_invoice"
  | "license_fee"

export type InvoiceGroupedItem = {
  +clientName: string,
  +cost: string,
  +items: $ReadOnlyArray<InvoiceItem>,
}

export type InvoiceItem = {
  +id: number,
  +clientName?: string,
  +description: string,
  +babyListItemId: number,
  +babyListInvoiceId: number,
  +babyListInvoicePdfPath: string,
  +babyListInvoiceCsvPath: string,
  +amount: number,
  +cost: string,
  +costBase: string,
}

export type InvoiceTotals = {
  +totalItemsInclVat: string,
  +totalItemsExclVat: string,
  +totalInclVat: string,
}

export type InvoiceState = {
  +invoice: ?Invoice,
  +itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
  +isProcessing: boolean,
  +finaliseError: boolean,
}

// And ItemToInvoice strongly resembles a BabyListItem, but has a 'amount' field
export type ItemToInvoice = SearchItem & {
  amount: number,
}

export type Action =
  | {type: "SET_USER", user: User}
  | {type: "SET_PRODUCTS", products: $ReadOnlyArray<ProductsByCategory>}
  | {type: "TOGGLE_PRODUCT", product: ProductOrSet}
  | {type: "TOGGLE_REVEALED_PRODUCT", product: ProductOrSet}
  | {type: "RESET_TOGGLE_PRODUCT"}
  | {type: "SET_MEDIA", media: $ReadOnlyArray<ProductMedia>}
  | {type: "CLEAR_MEDIA"}
  | {type: "SET_BABY", baby: Baby}
  | {type: "SET_BABY_LIST", babyList: BabyList}
  | {type: "SET_OPTIONALS", optionals: $ReadOnlyArray<BabyListOptionalItem>}
  | {type: "SET_SURVEY", survey: Survey}
  | {type: "START_BABY_SAVING"}
  | {type: "STOP_BABY_SAVING"}
  | {type: "START_INTAKE_SAVING"}
  | {type: "STOP_INTAKE_SAVING"}
  | {type: "SET_TASKS", tasks: $ReadOnlyArray<Task>}
  | {type: "SET_PLANNING", planning: $ReadOnlyArray<PlanningItem>}
  | {type: "SET_PLANNING_BABY", babyToken: ?Token}
  | {type: "SET_BABY_LISTS", babyLists: $ReadOnlyArray<BabyList>}
  | {type: "SET_SEARCH_ITEMS", searchItems: $ReadOnlyArray<SearchItem>}
  | {type: "SET_BABIES", babies: $ReadOnlyArray<Baby>}
  | {type: "SET_BABIES_MADAMS", babies: $ReadOnlyArray<BabyMadam>}
  | {type: "SET_OFFERS", offers: $ReadOnlyArray<BabyListOffer>}
  | {type: "SET_OFFER_SENDING", offerSending: boolean}
  | {type: "SET_BABY_STATE", id: number, state: BabyState}
  | {type: "SET_BABIES_FILTER", filter: BabiesFilterType, value: any}
  | {type: "SET_BABIES_LIST_TYPE", listType: ListType}
  | {type: "SET_BABIES_QUERY", query: string}
  | {type: "TOGGLE_BABIES_SORT_BY", sortByProp: BabiesSortByProperty}
  | {type: "RESET_BABIES_FILTERS"}
  | {type: "SET_BABIES_BABY", baby: Baby}
  | {type: "TOGGLE_SEARCH_LIST_ITEM", productId: number}
  | {type: "TOGGLE_FOUND_REMARKS_ITEM", searchItemId: number}
  | {type: "ACTIVATE_ALL_FOUND_REMARKS_ITEMS"}
  | {type: "CLOSE_ALL_SEARCH_ITEMS"}
  | {
      type: "ACTIVATE_FOUND_REMARKS_ITEMS",
      searchItemIds: $ReadOnlyArray<number>,
    }
  | {type: "DEACTIVATE_ALL_FOUND_REMARKS_ITEMS"}
  | {type: "SET_ALL_SEARCH_ITEMS_AS_FOUND"}
  | {
      type: "SET_SEARCH_ITEM_FOUND_STATE",
      productId: number,
      babyListItemFoundId: number,
      searchListItemId: number,
      state: ?BabyListItemFoundStateType,
      remarks: string,
      isPhotographed: boolean,
    }
  | {
      type: "SET_SEARCH_ITEM_PHOTOGRAPHED_LOCAL",
      babyListItemFoundIds: $ReadOnlyArray<number>,
    }
  | {
      type: "SET_SEARCH_ITEM_FOUND_STATE_BULK",
      productId: number,
      babyListItemFoundIds: $ReadOnlyArray<number>,
      searchListItemIds: $ReadOnlyArray<number>,
      state: ?BabyListItemFoundStateType,
    }
  | {type: "SET_SHOP_ITEMS", shopItems: $ReadOnlyArray<ShopItem>}
  | {type: "SET_SHOP", shop: Shop}
  | {type: "SET_SHOP_SEARCH_ITEMS", searchItems: $ReadOnlyArray<SearchItem>}
  | {type: "SET_SHOP_PROCESSING", isProcessing: boolean}
  | {type: "TOGGLE_SHOP_SELECTED_SEARCH_ITEM", searchItemKey: string}
  | {type: "TOGGLE_SELECTED_SHOP_ITEM", shopItemId: number}
  | {type: "UNSELECT_SHOP_ITEMS"}
  | {
      type: "SET_SHOP_ITEM_MEDIA",
      babyMedia: $ReadOnlyArray<Media>,
      shopItemMedia: $ReadOnlyArray<Media>,
    }
  | {type: "CLEAR_SHOP_ITEM_MEDIA"}
  | {type: "SELECT_ALL_SHOP_ITEMS"}
  | {type: "CLEAR_SELECTED_SEARCH_ITEMS"}
  | {type: "SET_BABY_LIST_INVOICE", babyListInvoice: BabyListInvoice}
  | {type: "SET_BABY_LIST_INVOICE_FINALISE_ERROR", finaliseError: boolean}
  | {type: "SET_BABY_LIST_INVOICE_PROCESSING", isProcessing: boolean}
  | {
      type: "SET_BABY_LIST_INVOICE_SEARCH_ITEMS",
      searchItems: $ReadOnlyArray<BabyListItem>,
    }
  | {
      type: "SET_BABY_LIST_INVOICE_ITEMS_TO_INVOICE",
      itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
    }
  | {type: "SET_INVOICE_FINALISE_ERROR", finaliseError: boolean}
  | {
      type: "SET_INVOICE_ITEMS_TO_INVOICE",
      itemsToInvoice: $ReadOnlyArray<ItemToInvoice>,
    }
  | {type: "SET_INVOICE", invoice: Invoice}
  | {type: "SET_INVOICE_PROCESSING", isProcessing: boolean}

export type Dispatch = (
  action: Action | ThunkAction | PromiseAction | Array<Action>
) => any
export type GetState = () => State
export type ThunkAction = (dispatch: Dispatch, getState: GetState) => any
export type PromiseAction = Promise<Action>

export type ApiClient = {
  +token: string,
}

export type ApiResponse = any
export type ApiMethod = "GET" | "POST" | "DELETE" | "PUT"
export type ApiClientOptions = any

export type Supplier = {
  id: number,
  name: string,
}
